// Hook owning the field room-rename (alias-edit) state machine (FFR2),
// extracted from building-capture-content.tsx so that file stays under the
// lint:max-lines cap — same split rationale as useDeleteRoom /
// useCancelSession.
//
// Responsibilities:
//   - track the room pending rename (open/close the dialog),
//   - keep a map of optimistic alias overrides keyed by space id so the
//     room list's secondary label updates the instant the user saves,
//   - dispatch the `updateSpaceAlias` mutation, and reconcile:
//       success → keep the optimistic override (the dispatch already wrote
//                 the local mirror; the override covers the REST adapter
//                 path where the list comes from a server DTO snapshot),
//       error   → roll the override back to the room's prior alias and
//                 surface the message via `.error`.
//
// The hook does NOT navigate. Offline-capable: the dispatch routes through
// the capture client, which (PowerSync adapter) writes the local row +
// queues a spaces-PATCH for upload on reconnect, or (REST adapter) PUTs the
// alias endpoint directly.

import { useCallback, useState } from 'react'

import { ApiError } from '../../hooks/api'
import type { CaptureClient } from '../client/capture-client.types'

export interface EditAliasTarget {
  id: string
  /** Canonical generated name (e.g. "Office 3"), shown in the dialog. */
  displayName: string
  /** The room's current alias (null when none), prefilled into the input. */
  alias: string | null
}

export interface UseEditRoomAliasInput {
  captureClient: Pick<CaptureClient, 'dispatch'>
  /** The owning capture session id (scopes + authorises the edit). */
  sessionId: string
}

export interface UseEditRoomAliasResult {
  /** The room currently pending rename, or null when no dialog open. */
  target: EditAliasTarget | null
  /**
   * Optimistic alias overrides keyed by space id. The caller applies these
   * to its room list so a rename shows immediately (and survives a server
   * DTO snapshot that predates the edit). A space absent from the map keeps
   * its own alias.
   */
  aliasOverrides: ReadonlyMap<string, string | null>
  isSaving: boolean
  error: string | null
  /** Open the rename dialog for a room. */
  open: (room: EditAliasTarget) => void
  /** Dismiss without saving (no-op while a save is in flight). */
  close: () => void
  /**
   * Confirm + dispatch the rename with the new alias (null clears it).
   * Optimistically applies the override, then reconciles. Resolves true on
   * success, false on failure (message exposed via `.error`).
   */
  confirm: (nextAlias: string | null) => Promise<boolean>
}

function describeAliasError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export function useEditRoomAlias({
  captureClient,
  sessionId,
}: UseEditRoomAliasInput): UseEditRoomAliasResult {
  const [target, setTarget] = useState<EditAliasTarget | null>(null)
  const [aliasOverrides, setAliasOverrides] = useState<
    ReadonlyMap<string, string | null>
  >(() => new Map<string, string | null>())
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = useCallback((room: EditAliasTarget) => {
    setError(null)
    setTarget(room)
  }, [])

  const close = useCallback(() => {
    if (isSaving) return
    setTarget(null)
    setError(null)
  }, [isSaving])

  const confirm = useCallback(
    async (nextAlias: string | null): Promise<boolean> => {
      if (target === null || isSaving) return false
      const room = target
      // The value to fall back to if the dispatch fails — the room's alias
      // as it was BEFORE this edit (could be a prior override or the
      // room's own alias). Reading the live map keeps a rapid second edit
      // correct.
      const priorAlias = aliasOverrides.has(room.id)
        ? (aliasOverrides.get(room.id) ?? null)
        : room.alias
      setIsSaving(true)
      setError(null)
      // Optimistic override — show the new name immediately.
      setAliasOverrides((prev) => {
        const next = new Map(prev)
        next.set(room.id, nextAlias)
        return next
      })
      try {
        const result = await captureClient.dispatch({
          kind: 'updateSpaceAlias',
          sessionId,
          spaceId: room.id,
          alias: nextAlias,
        })
        if (result.kind !== 'updateSpaceAlias') {
          throw new Error('Unexpected rename result kind')
        }
        // Reconcile the override to the authoritative (normalised) value
        // the server / local write persisted, so a trim the backend
        // applied is reflected.
        setAliasOverrides((prev) => {
          const next = new Map(prev)
          next.set(room.id, result.alias)
          return next
        })
        setIsSaving(false)
        setTarget(null)
        return true
      } catch (err) {
        // Roll the override back to the prior alias + surface the message.
        setAliasOverrides((prev) => {
          const next = new Map(prev)
          next.set(room.id, priorAlias)
          return next
        })
        setIsSaving(false)
        setError(describeAliasError(err))
        return false
      }
    },
    [captureClient, sessionId, target, isSaving, aliasOverrides],
  )

  return { target, aliasOverrides, isSaving, error, open, close, confirm }
}
